import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicHeader from "@/components/layout/PublicHeader";
import { api } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { ArrowRight, Users, Globe, Wallet, Check } from "lucide-react";

const HOW_IT_WORKS = [
  { num: "01", t: "Sign your agency up", d: "Free verification. You get an Agency dashboard within 24 h." },
  { num: "02", t: "Upload workers to the Premium list", d: "Each upload is reviewed by Maqam staff. Approved workers go live on the Hire dashboard." },
  { num: "03", t: "Maqam markets, employers find", d: "Every employer paying $700/mo browses the Premium list first." },
  { num: "04", t: "You earn commission on every placement", d: "Currently 15 % of the first-month salary. Paid out 30 days post-hire." },
];

export default function Agencies() {
  useSEO({
    title: "Agencies — Maqam talent marketplace",
    description: "Upload your roster to Maqam's curated Premium list and earn commission every time an employer hires one of your workers.",
    path: "/agencies",
  });

  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/premium/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#0c0c0d] text-white" data-testid="agencies-page">
      <PublicHeader />

      {/* Hero */}
      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-6">/ FOR AGENCIES · COMMISSION-BASED</div>
          <h1 className="font-display font-black text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9] max-w-4xl">
            Plug your roster
            <br />
            <span className="italic text-white/60">into the network.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg md:text-xl text-white/70 leading-relaxed">
            Maqam is the spider-web that connects every recruitment agency to
            every employer paying for top talent in Saudi Arabia and the GCC.
            Upload your workers, get them reviewed, and <span className="text-white">earn commission</span> on every placement.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link
              to="/agencies/register"
              className="inline-flex items-center justify-center gap-3 bg-white text-black hover:bg-white/85 px-8 py-5 font-bold transition-colors"
              data-testid="agencies-cta-signup"
            >
              Register your agency <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href="#how"
              className="inline-flex items-center justify-center gap-3 border border-white/30 hover:border-white px-8 py-5 font-bold transition-colors"
            >
              How it works
            </a>
          </div>

          {/* Stats */}
          <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-6 max-w-4xl">
            {[
              { num: stats?.approved_count ?? "—",  label: "Premium workers live" },
              { num: stats?.agencies_count ?? "—",  label: "Active agencies" },
              { num: stats?.countries_count ?? "9", label: "Countries sourced" },
              { num: "15%",                         label: "Commission per hire" },
            ].map((s) => (
              <div key={s.label}>
                <div className="font-display font-black text-4xl md:text-5xl tracking-tighter">{s.num}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="border-b border-white/10 bg-[#141416]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32 grid lg:grid-cols-3 gap-8">
          {[
            { icon: Users, t: "Roster, not résumés", d: "You know your workers. Just upload their core profile — we won't make you fill out a 30-page form." },
            { icon: Globe, t: "All 9 source countries", d: "KSA, UAE, Qatar, UK, Germany, France, India, Philippines, Canada — employers filter by country, you compete in your niche." },
            { icon: Wallet, t: "Commission, no upfront fees", d: "Listing is free. We only earn when employers hire your workers. Aligned incentives." },
          ].map((v) => (
            <div key={v.t} className="border border-white/10 p-8 hover:border-white/30 transition-colors">
              <v.icon className="w-8 h-8 mb-5" strokeWidth={1.5} />
              <h3 className="font-display font-bold text-2xl mb-3 tracking-tight">{v.t}</h3>
              <p className="text-white/65 text-sm leading-relaxed">{v.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-4">/ HOW IT WORKS</div>
          <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-16 max-w-2xl">
            Four steps from signup to first commission.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 border border-white/10">
            {HOW_IT_WORKS.map((s, i) => (
              <div key={s.num} className={`p-8 ${i < HOW_IT_WORKS.length - 1 ? "border-b lg:border-b-0 lg:border-r border-white/10" : ""} hover:bg-white/[0.02] transition-colors`}>
                <div className="font-display font-black text-5xl tracking-tighter text-white/20 mb-6">{s.num}</div>
                <h3 className="font-display font-bold text-xl mb-3 tracking-tight">{s.t}</h3>
                <p className="text-white/55 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section>
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-24 lg:py-32 text-center">
          <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter mb-6">
            Stop leaving placements on the table.
          </h2>
          <p className="text-white/65 text-lg mb-10 max-w-2xl mx-auto">
            Join the network of agencies feeding Maqam's $700/mo employer base.
            Free to sign up. Free to list. You only earn when we earn.
          </p>
          <ul className="inline-block text-left mb-10 space-y-2 text-white/75">
            {[
              "Free agency dashboard",
              "Unlimited worker uploads",
              "15 % commission on every placement",
              "Verified Agency badge after first 3 approved workers",
            ].map((f) => (
              <li key={f} className="flex items-start gap-3"><Check className="w-4 h-4 text-[#34C759] flex-shrink-0 mt-1" />{f}</li>
            ))}
          </ul>
          <div>
            <Link
              to="/agencies/register"
              className="inline-flex items-center gap-3 bg-white text-black hover:bg-white/85 px-10 py-5 font-bold transition-colors"
            >
              Register your agency <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
